import React from 'react';
import { Shield, ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => window.location.href = '/'}
            className="flex items-center text-[#003087] hover:underline mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Home
          </button>
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_apagency-farmers/artifacts/m4aqjodf_image.jpg" 
              alt="AP Agency Logo" 
              className="h-20 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <Shield className="h-8 w-8 text-[#003087] mr-3" />
            <h1 className="text-3xl font-bold text-[#003087]">Privacy Policy</h1>
          </div>
          <p className="text-gray-600 mt-2 text-center">Last Updated: February 13, 2026</p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 prose prose-blue max-w-none">
          
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              AP Agency ("we," "us," or "our") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website <strong>apagency.us</strong> (the "Site") or interact with our services.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By using our Site or services, you consent to the data practices described in this policy. If you do not agree with this Privacy Policy, please do not access or use our Site.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-[#003087] mb-3">2.1 Personal Information</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              We collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Request an insurance quote through our online form</li>
              <li>Schedule an appointment with one of our agents</li>
              <li>Contact us via phone, email, or live chat</li>
              <li>Subscribe to our newsletter or marketing communications</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              This information may include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Full name (first and last)</li>
              <li>Date of birth</li>
              <li>Physical address (street, city, state, zip code)</li>
              <li>Phone number (mobile or landline)</li>
              <li>Email address</li>
              <li>Insurance coverage needs and preferences</li>
              <li>Any additional information you choose to provide</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">2.2 Automatically Collected Information</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              When you visit our Site, we automatically collect certain information about your device and browsing activity, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>IP address and geographic location</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
              <li>Clickstream data and navigation patterns</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">2.3 Cookies and Tracking Technologies</h3>
            <p className="text-gray-700 leading-relaxed">
              We use cookies, web beacons, and similar tracking technologies to enhance your experience on our Site. These technologies help us understand user behavior, remember your preferences, and improve our services. You can control cookies through your browser settings, but disabling them may limit your ability to use certain features of our Site.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>To provide insurance quotes:</strong> Process your quote requests and match you with appropriate insurance products</li>
              <li><strong>To communicate with you:</strong> Respond to inquiries, schedule appointments, and provide customer support</li>
              <li><strong>To improve our services:</strong> Analyze Site usage and user feedback to enhance our offerings</li>
              <li><strong>To send marketing communications:</strong> With your consent, send promotional emails, newsletters, and special offers</li>
              <li><strong>To comply with legal obligations:</strong> Meet regulatory requirements and respond to lawful requests</li>
              <li><strong>To prevent fraud:</strong> Detect and prevent fraudulent activities and protect our users</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">4. Text Messaging (SMS) and TCR Compliance</h2>
            
            <h3 className="text-xl font-semibold text-[#003087] mb-3">4.1 Consent to Receive Text Messages</h3>
            <p className="text-gray-700 leading-relaxed">
              By providing your mobile phone number and opting in to receive text messages from AP Agency, you expressly consent to receive:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 my-3">
              <li>Appointment reminders and confirmations</li>
              <li>Insurance quote updates and follow-ups</li>
              <li>Policy renewal notifications</li>
              <li>Promotional offers and insurance tips (if you opt in to marketing messages)</li>
              <li>Service-related alerts and updates</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">4.2 Message Frequency</h3>
            <p className="text-gray-700 leading-relaxed">
              The frequency of text messages will vary depending on your interactions with us. You may receive:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 my-3">
              <li><strong>Transactional messages:</strong> As needed for appointments, quotes, and policy updates</li>
              <li><strong>Marketing messages (if opted in):</strong> Up to 4 messages per month</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">4.3 Message and Data Rates</h3>
            <p className="text-gray-700 leading-relaxed">
              Message and data rates may apply based on your mobile carrier's plan. Please check with your carrier for details. We are not responsible for any charges incurred from your mobile carrier.
            </p>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">4.4 How to Opt Out</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              You can opt out of receiving text messages at any time by:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Replying <strong>STOP</strong>, <strong>UNSUBSCRIBE</strong>, <strong>CANCEL</strong>, <strong>END</strong>, or <strong>QUIT</strong> to any message</li>
              <li>Contacting us at (970) 315-4628 or staff@apagency.us</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              After you opt out, you will receive a confirmation message and will no longer receive marketing text messages. However, you may still receive transactional messages related to active insurance quotes or policies.
            </p>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">4.5 Help and Support</h3>
            <p className="text-gray-700 leading-relaxed">
              For help with text messages, reply <strong>HELP</strong> to any message or contact us at (970) 315-4628.
            </p>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">4.6 The Campaign Registry (TCR) Compliance</h3>
            <p className="text-gray-700 leading-relaxed">
              AP Agency is registered with The Campaign Registry (TCR) for all SMS campaigns. We comply with all applicable regulations including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 my-3">
              <li>Telephone Consumer Protection Act (TCPA)</li>
              <li>CAN-SPAM Act</li>
              <li>CTIA Messaging Principles and Best Practices</li>
              <li>Mobile carrier requirements and guidelines</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">4.7 No Condition of Purchase</h3>
            <p className="text-gray-700 leading-relaxed">
              Your consent to receive text messages is not a condition of purchasing any goods or services from AP Agency. You can still receive insurance quotes and services without opting in to text messages.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">5. Sharing Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may share your information with the following parties:
            </p>

            <h3 className="text-xl font-semibold text-[#003087] mb-3">5.1 Insurance Carriers</h3>
            <p className="text-gray-700 leading-relaxed">
              As an authorized representative of Farmers Insurance, we share your information with Farmers Insurance Group of Companies and their affiliated companies for the purpose of obtaining the insurance quotes that you have requested. In cases where the risk falls outside of Farmers Insurance underwriting appetite, we may share your information with other insurance carriers to obtain competitive quotes on your behalf. The information shared is limited to what is necessary to process your quote requests, underwrite policies, and provide insurance services. Each carrier maintains its own privacy policy governing their use of your information.
            </p>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">5.2 Service Providers</h3>
            <p className="text-gray-700 leading-relaxed">
              We work with third-party service providers who assist us in operating our website, conducting our business, and servicing you. These providers include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 my-3">
              <li>CRM and database management systems</li>
              <li>Email and SMS marketing platforms</li>
              <li>Calendar and scheduling tools</li>
              <li>Website analytics and optimization tools</li>
              <li>Payment processors</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
            </p>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">5.3 Legal Requirements</h3>
            <p className="text-gray-700 leading-relaxed">
              We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, subpoenas, or government agencies).
            </p>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">5.4 Business Transfers</h3>
            <p className="text-gray-700 leading-relaxed">
              In the event of a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred to the acquiring entity.
            </p>

            <h3 className="text-xl font-semibold text-[#003087] mb-3 mt-6">5.5 No Sale of Personal Information</h3>
            <p className="text-gray-700 leading-relaxed">
              AP Agency does not sell your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">6. Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 my-3">
              <li>Encryption of data in transit (SSL/TLS)</li>
              <li>Secure data storage with access controls</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Employee training on data protection and privacy</li>
              <li>Bot protection and fraud detection systems (Cloudflare Turnstile)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee its absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">7. Your Privacy Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Depending on your location, you may have certain rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
              <li><strong>Opt-out:</strong> Opt out of receiving marketing communications</li>
              <li><strong>Data portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise any of these rights, please contact us at (970) 315-4628 or staff@apagency.us. We will respond to your request within 30 days.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">8. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our Site and services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately so we can delete it.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">9. Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Our Site may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 my-3">
              <li>Posting the updated policy on our Site with a new "Last Updated" date</li>
              <li>Sending you an email notification (if you have provided your email address)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Your continued use of our Site or services after any changes indicates your acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">11. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-[#b4c4ea]/20 rounded-lg p-6">
              <p className="font-semibold text-[#003087] mb-2">AP Agency</p>
              <p className="text-gray-700">383 W 37th St Ste 202</p>
              <p className="text-gray-700">Loveland, CO 80538</p>
              <p className="text-gray-700 mt-3"><strong>Phone:</strong> (970) 315-4628</p>
              <p className="text-gray-700"><strong>Email:</strong> staff@apagency.us</p>
              <p className="text-gray-700 mt-3"><strong>License:</strong> CO License #852298</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-[#003087] mb-4">12. Colorado Privacy Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              As a Colorado resident, you have the following rights under the Colorado Privacy Act:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Right to Access:</strong> You may request confirmation of whether we are processing your personal data and access to such data.</li>
              <li><strong>Right to Correction:</strong> You may request correction of inaccuracies in your personal data.</li>
              <li><strong>Right to Deletion:</strong> You may request deletion of your personal data.</li>
              <li><strong>Right to Data Portability:</strong> You may request a copy of your personal data in a portable and readily usable format.</li>
              <li><strong>Right to Opt Out:</strong> You may opt out of the processing of your personal data for purposes of targeted advertising, the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects.</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise these rights, please contact us at (970) 315-4628 or staff@apagency.us. We will respond to your request within 45 days.
            </p>
          </section>

          <div className="bg-blue-50 border-l-4 border-[#003087] p-6 mt-8">
            <p className="text-sm text-gray-700">
              <strong>Effective Date:</strong> This Privacy Policy is effective as of February 13, 2026 and governs our collection, use, and disclosure of your information from that date forward.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
